import { useMemo, useRef, useState } from 'react';

import {
  type DropdownOption,
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  FormLabel,
  ModalV2,
  Text,
  UniversalSelect,
  useToastMessage,
} from '@equip.health/ui';

const TOP_LEVEL_ID = 'group-class-archive-modal';

interface GroupClassArchiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupClassExternalId: string;
  onSuccess: () => void;
}

import { GroupClassEvents } from '~/analytics/groupClassEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  useArchiveGroupClassMutation,
  useGetArchiveReasonsListQuery,
} from '~/store/services/groupClass';

const GroupClassArchiveModal = ({
  isOpen,
  groupClassExternalId,
  onClose,
  onSuccess,
}: GroupClassArchiveModalProps) => {
  const hasShownToast = useRef(false);
  const [selectedArchiveReason, setSelectedArchiveReason] =
    useState<DropdownOption>();

  const [archiveGroupClassRequest] = useArchiveGroupClassMutation();

  const { data: archiveReasonsResponse } = useGetArchiveReasonsListQuery();
  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();
  const handleModalClose = () => {
    setSelectedArchiveReason(null);
    onClose();
  };

  const archiveReasons: DropdownOption[] = useMemo(() => {
    if (archiveReasonsResponse) {
      return archiveReasonsResponse.data.map(
        (each: GroupClassArchivalReasonResponseItem) => ({
          label: each.archiveReason,
          value: each.groupArchiveReasonId,
        }),
      );
    }
    return [];
  }, [archiveReasonsResponse]);

  const resetHasShownToastRef = () => {
    hasShownToast.current = true;
    setTimeout(() => {
      hasShownToast.current = false;
    }, 2000);
  };

  const archiveErrorToast = () => {
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: 'archive-group-class-error_toast',
      text: "We're sorry, we were unable to archive the group at this time. Please try again.",
      variant: 'critical',
    });
    resetHasShownToastRef();
  };

  const archiveSuccessToast = () => {
    createToast({
      icon: FAIconName.checkRegular,
      id: 'archive-group-class-success_toast',
      text: 'Group class archived successfully.',
      variant: 'success',
    });
    onSuccess();
    resetHasShownToastRef();
    handleModalClose();
  };

  const handleOnArchive = () => {
    trackEvent(GroupClassEvents.archiveGroupFromTheConfirmationModal, {
      Source: AnalyticsSourceValues.CALENDAR_GROUPS_TAB,
    });
    archiveGroupClassRequest({
      archiveReasonId: selectedArchiveReason.value,
      groupClassExternalId,
    })
      .unwrap()
      .then(archiveSuccessToast)
      .catch(archiveErrorToast);
  };

  return (
    <ModalV2
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={handleModalClose}
      size="md"
      title="Archive group"
    >
      <Text>
        Please note, archiving this group will cancel any future sessions
        immediately and you will not be able to restart the group again. If this
        group previously had an end date, archiving it now will override that
        end date.
      </Text>
      <ChakraBox gap="6px" marginTop="24px">
        <FormLabel
          id={`${TOP_LEVEL_ID}__reason-label`}
          label="Archive reason"
        />
      </ChakraBox>
      <ChakraBox marginTop="6px">
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__archive-reasons-dropdown`}
          isSingleSelect
          modalId={TOP_LEVEL_ID}
          onChange={(archiveReason: DropdownOption) => {
            setSelectedArchiveReason(archiveReason);
          }}
          options={archiveReasons || []}
          placeholder="Please choose"
        />
      </ChakraBox>
      <ChakraFlex justifyContent="space-between" marginTop="24px">
        <FireflyButton
          id={`${TOP_LEVEL_ID}__cancel-button`}
          leftIcon={FAIconName.timesRegular}
          onClick={() => handleModalClose()}
          variant="secondary"
        >
          Cancel
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__confirm-button`}
          isDisabled={!selectedArchiveReason}
          leftIcon={FAIconName.trashRegular}
          onClick={() => handleOnArchive()}
          variant="accent.critical"
        >
          Archive group
        </FireflyButton>
      </ChakraFlex>
    </ModalV2>
  );
};

export default GroupClassArchiveModal;
